import React, { useState, useEffect } from "react";
import axios from "axios";
import { X, Download, Loader2, CheckCircle2, Gift, Sparkles } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { useToast } from "../hooks/use-toast";

const API = process.env.REACT_APP_BACKEND_URL;

const BENEFITS = [
  "Le 7 competenze digitali più richieste dalle aziende nel 2026",
  "Guida pratica: da dove partire per trasformarle in reddito",
  "Bonus: come integrare AI, tecnologia e sostenibilità nel tuo lavoro",
];

export default function LeadMagnetModal({ open, onClose, fonte = "home" }) {
  const [email, setEmail] = useState("");
  const [nome, setNome] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [submitting, setSubmitting] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState("");
  const { toast } = useToast();

  useEffect(() => {
    if (!open) {
      setEmail("");
      setNome("");
      setWebsite("");
      setDownloadUrl("");
      setSubmitting(false);
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast({ description: "Inserisci un'email valida.", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/lead-magnet/subscribe`, {
        email: email.trim().toLowerCase(),
        nome: nome.trim(),
        fonte,
        website,
      });
      const url = res.data?.download_url;
      if (url) {
        const abs = url.startsWith("http") ? url : `${API}${url}`;
        setDownloadUrl(abs);
        // Auto-open PDF in new tab dopo un istante
        setTimeout(() => window.open(abs, "_blank", "noopener,noreferrer"), 300);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast({
        description: typeof detail === "string" ? detail : "Errore invio richiesta. Riprova.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="lead-magnet-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        <button
          onClick={onClose}
          data-testid="lead-magnet-close"
          className="absolute top-4 right-4 h-9 w-9 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 flex items-center justify-center z-10"
          aria-label="Chiudi"
        >
          <X className="h-5 w-5" />
        </button>

        {downloadUrl ? (
          // ---- Thank you screen ----
          <div className="p-8 md:p-10 text-center" data-testid="lead-magnet-success">
            <div className="h-16 w-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-8 w-8" />
            </div>
            <h3 className="text-2xl font-extrabold text-[#0a1f55]">
              Grazie! Il PDF è pronto.
            </h3>
            <p className="mt-3 text-slate-600 leading-relaxed">
              Ti abbiamo inviato una copia anche via email a <strong>{email}</strong>.
              Il download dovrebbe partire in automatico.
            </p>
            <a href={downloadUrl} target="_blank" rel="noreferrer">
              <Button
                data-testid="lead-magnet-download-btn"
                className="mt-6 bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1f55] font-extrabold h-12 px-6"
              >
                <Download className="mr-2 h-4 w-4" />
                Scarica di nuovo il PDF
              </Button>
            </a>
            <div className="mt-6 pt-6 border-t border-slate-100">
              <div className="text-xs uppercase tracking-widest font-bold text-[#f59e0b] mb-2">
                Passo successivo
              </div>
              <p className="text-sm text-slate-600">
                Vuoi trasformare queste competenze in una professione certificata?
              </p>
              <a href="/corso">
                <Button
                  data-testid="lead-magnet-corso-cta"
                  variant="outline"
                  className="mt-3 h-10 border-[#0a1f55] text-[#0a1f55] hover:bg-[#0a1f55] hover:text-white"
                >
                  Scopri il corso da € 99
                </Button>
              </a>
            </div>
          </div>
        ) : (
          // ---- Form ----
          <>
            <div className="relative bg-gradient-to-br from-[#0a1f55] via-[#102a6b] to-[#1e3a8a] px-6 py-8 md:px-8">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-xl bg-[#f59e0b] text-[#0a1f55] flex items-center justify-center">
                  <Gift className="h-6 w-6" />
                </div>
                <div>
                  <div className="text-[11px] font-extrabold tracking-[0.22em] uppercase text-[#fbbf24]">
                    Guida gratuita · PDF
                  </div>
                  <h2 className="text-xl md:text-2xl font-extrabold text-white leading-tight">
                    Le 7 competenze digitali del 2026
                  </h2>
                </div>
              </div>
            </div>

            <form onSubmit={submit} className="p-6 md:p-8 space-y-4">
              <ul className="space-y-2.5">
                {BENEFITS.map((b, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-[14px] text-slate-700 leading-relaxed">
                    <Sparkles className="h-4 w-4 mt-0.5 text-[#f59e0b] flex-shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>

              {/* honeypot */}
              <input
                type="text"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                className="absolute -left-[9999px] w-px h-px opacity-0"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />

              <div className="grid grid-cols-1 gap-3 pt-2">
                <div>
                  <Input
                    data-testid="lead-magnet-nome"
                    placeholder="Il tuo nome (facoltativo)"
                    value={nome}
                    onChange={(e) => setNome(e.target.value)}
                    className="h-11"
                  />
                </div>
                <div>
                  <Input
                    data-testid="lead-magnet-email"
                    type="email"
                    required
                    placeholder="La tua email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-11"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                data-testid="lead-magnet-submit"
                className="w-full h-12 bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1f55] font-extrabold text-base"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Invio in corso…
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" />
                    Scarica gratis il PDF
                  </>
                )}
              </Button>

              <p className="text-[11.5px] text-slate-500 text-center leading-relaxed">
                Iscrivendoti riceverai il PDF e contenuti sulla professione del Consulente Digitale.
                Nessuno spam, puoi disiscriverti quando vuoi.
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
